import { computed, inject, Injectable, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { filter, Observable, switchMap } from 'rxjs';

import { LocalDate, TzDate } from '@fcom/core/utils/tz-date';
import { FragmentService } from '@fcom/common/services/fragments/fragment.service';
import { CmsArticle } from '@fcom/core-api';
import { BookingAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { cartData } from '@fcom/booking/store/selectors/cart.selector';
import { FinnairCart } from '@fcom/dapi/api/models/finnair-cart';
import { CountryCode } from '@fcom/booking/utils/cart-data.utils';

@Injectable({ providedIn: 'root' })
export class MoneyBackGuaranteeService {
  private store$: Store<BookingAppState> = inject(Store);
  private fragmentService = inject(FragmentService);

  readonly content$: Observable<CmsArticle | null>;
  readonly isValidDate: Signal<boolean>;
  private readonly currentOffer: Signal<FinnairCart>;

  constructor() {
    this.currentOffer = toSignal(this.store$.pipe(cartData()), { initialValue: null });

    this.isValidDate = computed(() => {
      const cart = this.currentOffer();
      const isAward = !!cart?.prices?.unpaid?.total?.total?.totalPoints;
      if (!cart?.bounds?.length || isAward) {
        return false;
      }

      if (this.isOriginKorea(cart)) {
        return true;
      }

      const departureDateTime = LocalDate.fromTzDate(TzDate.of(cart.bounds[0].departure.dateTime));
      const now = LocalDate.now();

      return departureDateTime.minusDays(7).gt(now);
    });

    this.content$ = toObservable(this.isValidDate).pipe(
      filter(Boolean),
      switchMap(() => this.fragmentService.getFragment<CmsArticle>('fragments.moneyBackGuarantee.url'))
    );
  }

  private isOriginKorea(cart: FinnairCart): boolean {
    const originLocationCode = cart.bounds?.[0]?.departure?.locationCode;
    const originCountryCode = originLocationCode ? cart.locations?.[originLocationCode]?.countryCode : undefined;
    return originCountryCode === CountryCode.KOREA;
  }
}
